import { defineStore } from "pinia";

export const usePollStore = defineStore({
  id: "poll",
  state: () => ({
    pollPlz: "0",
    title: "",
    useGeneralSettings: true,
    externalPollLink: undefined,
    alternativeExternalPollLink: undefined,
    introductionSteps: [],
    introductionText: "",
    introductionTitle: "",
    pollDate: undefined,
    showCustomerLogo: false,
    pollMonthsInOrganization: undefined,
    pollWorkingDays: undefined,
    pollHomeofficeDays: undefined,
    pollMonthsInOrganizationMax: undefined,
    pollWorkingDaysMax: undefined,
    pollHomeofficeDaysMax: undefined,
    pollMonthsInUniversity: undefined,
    pollMonthsInUniversityMax: undefined,
    pollMonthsLecturePeriod: undefined,
    pollMonthsLecturePeriodMax: undefined,
    pollMonthsLectureFreePeriod: undefined,
    pollMonthsLectureFreePeriodMax: undefined,
    pollPresenceDaysInLecturePeriod: undefined,
    pollPresenceDaysInLecturePeriodMax: undefined,
    pollPresenceDaysInLectureFreePeriod: undefined,
    pollPresenceDaysInLectureFreePeriodMax: undefined,
    usePoll: false,
    // optional per-instance texts for the four numbered poll sections (situation, search, rating, submit);
    // undefined -> legacy rendering with title + introductionSteps
    sections: undefined,
    // when true the postal code must be a 5-digit number ('0' default is not accepted)
    requirePlz: false,
    // when true every transport variant with frequency > 0 must have a star rating
    requireStarsForUsedTransports: false,
    // when true every listed transport variant (used or not) must have a star rating
    requireStarsForAllTransports: false,
    frequencyType: 'days',
    pollType: 'company',
    frequencies: [],
    numberPassengers: [],
    numberStars: [],
    transportUsed: []
  }),
  getters: {
    pollFrequency: (state) => {
      return (transportId) => state.frequencies.find(f => f.transportId === transportId) ? state.frequencies.find(f => f.transportId === transportId).freq : 0
        ;
    },
    numberPassengersOfTransport: (state) => {
      return (transportId) => state.numberPassengers.find(f => f.transportId === transportId) ? state.numberPassengers.find(f => f.transportId === transportId).freq : 0;
    },
    isTransportUsed: (state) => {
      return (transportId) => state.transportUsed.find(f => f.transportId === transportId) ? state.transportUsed.find(f => f.transportId === transportId).used : false;
    },
    getNumberStars: (state) => {
      return (transportId) => state.numberStars.find(f => f.transportId === transportId) ? state.numberStars.find(f => f.transportId === transportId).freq : 0;
    },
    /** sum of all frequencies entered for the transport variants */
    daysDistributed: (state) => state.frequencies.reduce((sum, f) => sum + (Number(f.freq) || 0), 0),
    /** number of days (or percent) the user has to distribute over the transport variants */
    daysRequired: (state) => {
      if (state.frequencyType === 'percentage') {
        return 100;
      }
      const nDays = state.pollWorkingDays !== undefined
        ? Number(state.pollWorkingDays) - Number(state.pollHomeofficeDays || 0)
        : Number(state.pollPresenceDaysInLecturePeriod);
      return Number.isNaN(nDays) ? 0 : nDays;
    },
    /** ids of transport variants with a frequency > 0 */
    usedTransportIds: (state) => state.frequencies.filter(f => Number(f.freq) > 0).map(f => f.transportId),
    /** ids of transport variants with a star rating */
    ratedTransportIds: (state) => state.numberStars.filter(s => Number(s.freq) > 0).map(s => s.transportId),
    /** ids of all listed transport variants (a star entry is created for every route) */
    allTransportIds: (state) => state.numberStars.map(s => s.transportId),
    /** which variants need a rating: 'all', 'used' or false */
    starsRequiredFor: (state) =>
      state.requireStarsForAllTransports === true ? 'all'
        : state.requireStarsForUsedTransports === true ? 'used'
          : false,
    /** ids of the variants that must be rated according to the configured rule */
    transportIdsRequiringStars() {
      if (this.starsRequiredFor === 'all') return this.allTransportIds;
      if (this.starsRequiredFor === 'used') return this.usedTransportIds;
      return [];
    },
  },
  actions: {
    /**
     * reset just the survey input related to transport evaluation and choices 
     * (but doesn't concern the user's personal info like postal code or the months he is in the organization)
     */
    resetSuveyInput() {
      this.frequencies = [];
      this.numberPassengers = [];
      this.numberStars = [];
      this.transportUsed = [];
    },
    isPollPlzValid() {
      const plz = String(this.pollPlz ?? '');
      if (/^[0-9]{5}$/.test(plz)) {
        return true;
      }
      // the default '0' is only accepted when the postal code is not mandatory
      return !this.requirePlz && plz === '0';
    },
    /** whether a specific transport variant must be rated but has no stars yet */
    isTransportMissingRating(transportId) {
      if (this.pollType === 'event' || !this.starsRequiredFor) return false;
      const mustRate = this.starsRequiredFor === 'all' || this.pollFrequency(transportId) > 0;
      return mustRate && this.getNumberStars(transportId) === 0;
    },
    /** all variants that must be rated (all or only the used ones, see config) have stars */
    isPollStarsValid() {
      if (!this.starsRequiredFor || this.pollType === 'event') {
        return true;
      }
      return this.transportIdsRequiringStars.every(id => this.getNumberStars(id) > 0);
    },
    /** whether the situation fields (months, working days, homeoffice days) are valid */
    isPollSituationValid() {
      return this.isPollHomeofficeDaysValid()
        && this.isPollMonthsInOrganizationValid()
        && this.isPollWorkingDaysValid();
    },
    /**
     * list of keys of the poll items which are still missing before the poll can be submitted;
     * possible keys: 'situation', 'days', 'stars', 'transportUsed', 'plz'
     */
    missingPollItems() {
      const missing = [];
      if (!this.isPollSituationValid()) missing.push('situation');
      if (!this.isPollFrequenciesValid()) missing.push('days');
      if (!this.isPollStarsValid()) missing.push('stars');
      if (!this.isTransportsUsedValid()) missing.push('transportUsed');
      if (!this.isPollPlzValid()) missing.push('plz');
      return missing;
    },
    isPollMonthsInOrganizationValid() {
      if (
        this.pollMonthsInOrganization === undefined ||
        (Number.isInteger(Number(
          this.pollMonthsInOrganization)
        ) &&
          this.pollMonthsInOrganization <=
          this.pollMonthsInOrganizationMax
          && this.pollMonthsInOrganization >= 0
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
    isPollPresenceDaysInLectureFreePeriodValid() {
      if (
        this.pollPresenceDaysInLectureFreePeriod === undefined ||
        (Number.isInteger(Number(
          this.pollPresenceDaysInLectureFreePeriod)
        ) &&
          this.pollPresenceDaysInLectureFreePeriod <=
          this.pollPresenceDaysInLectureFreePeriodMax
          && this.pollPresenceDaysInLectureFreePeriod >= 0
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
    isPollPresenceDaysInLecturePeriodValid() {
      if (
        this.pollPresenceDaysInLecturePeriod === undefined ||
        (Number.isInteger(Number(
          this.pollPresenceDaysInLecturePeriod)
        ) &&
          this.pollPresenceDaysInLecturePeriod <=
          this.pollPresenceDaysInLecturePeriodMax
          && this.pollPresenceDaysInLecturePeriod >= 0
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
    isPollMonthsLectureFreePeriodValid() {
      if (
        this.pollMonthsLecturePeriod === undefined ||
        (Number.isInteger(Number(
          this.pollMonthsLecturePeriod)
        ) &&
          this.pollMonthsLecturePeriod <=
          this.pollMonthsLecturePeriodMax
          && this.pollMonthsLecturePeriod >= 0
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
    isPollMonthsLecturePeriodValid() {
      if (
        this.pollMonthsLecturePeriod === undefined ||
        (Number.isInteger(Number(
          this.pollMonthsLecturePeriod)
        ) &&
          this.pollMonthsLecturePeriod <=
          this.pollMonthsLecturePeriodMax
          && this.pollMonthsLecturePeriod >= 0
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
    isPollMonthsInUniversityValid() {
      if (
        this.pollMonthsInUniversity === undefined ||
        (Number.isInteger(Number(
          this.pollMonthsInUniversity)
        ) &&
          this.pollMonthsInUniversity <=
          this.pollMonthsInUniversityMax
          && this.pollMonthsInUniversity >= 0
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
    /*
    checking frequencies - depending on type percentage/year or days/week
    */
    isPollFrequenciesValid() {
      // event-type doesn't use frequencies
      if (this.pollType === 'event') {
        return true;
      }

      if (this.frequencies.length === 0) {
        return false;
      }
      else if (this.frequencyType === 'percentage') {
        const sum = this.frequencies.map((a) => a.freq).reduce((a, b) => a + b);

        return sum === 100;
      } else if (this.frequencyType === 'days') {
        const sum = this.frequencies.map((a) => a.freq).reduce((a, b) => a + b);
        const nDays = this.pollWorkingDays !== undefined ? this.pollWorkingDays - this.pollHomeofficeDays : this.pollPresenceDaysInLecturePeriod;
        return sum === Number(nDays);
      } else {
        return false;
      }
    },
    isTransportsUsedValid() {
      if (this.pollType === 'event') {
        const sum = this.transportUsed.filter(m => m === true);
        return sum.length === 1 ? true : false;


      } else if (this.pollType === 'company') {
        return true;
      }
    },
    isPollWorkingDaysValid() {
      if (
        this.pollWorkingDays === undefined ||
        (Number.isInteger(Number(
          this.pollWorkingDays)
        ) &&
          this.pollWorkingDays <=
          this.pollWorkingDaysMax
          && this.pollWorkingDays >= 0
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
    isPollHomeofficeDaysValid() {
      if (
        this.pollHomeofficeDays === undefined ||
        (Number.isInteger(Number(
          this.pollHomeofficeDays)
        ) &&
          this.pollHomeofficeDays <=
          this.pollHomeofficeDaysMax
          && this.pollHomeofficeDays >= 0
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
    /**
    * returns validity of pollData
    */
    isPollValid() {
      // if the general settings are valid and 
      if (
        this.isPollHomeofficeDaysValid() &&
        this.isPollMonthsInOrganizationValid() &&
        this.isPollWorkingDaysValid() && this.isPollPlzValid()
        && this.isPollFrequenciesValid()
        && this.isPollStarsValid()
        && this.isTransportsUsedValid()
      ) {
        return true;
      } else {
        return false;
      }
    },
    /**
* returns validity of pollData without considering the routes choices of user, as it will be done on the alternative external poll page
*/
    isAlternativePollValid() {
      // if the general settings are valid and 
      if (
        this.isPollHomeofficeDaysValid() &&
        this.isPollMonthsInOrganizationValid() &&
        this.isPollWorkingDaysValid() && this.isPollPlzValid()
      ) {
        return true;
      } else {
        return false;
      }
    },
    setGeneralPollSettings(generalSettings) {
      if (generalSettings) {
        this.useGeneralSettings = generalSettings.useGeneralSettings;
        this.pollMonthsInOrganization = generalSettings.pollMonthsInOrganization;
        this.pollWorkingDays = generalSettings.pollWorkingDays;
        this.pollHomeofficeDays = generalSettings.pollHomeofficeDays;
        this.pollMonthsInOrganizationMax = generalSettings.pollMonthsInOrganizationMax;
        this.pollWorkingDaysMax = generalSettings.pollWorkingDaysMax;
        this.pollHomeofficeDaysMax = generalSettings.pollHomeofficeDaysMax;
        this.pollMonthsInUniversity = generalSettings.pollMonthsInUniversity;
        this.pollMonthsInUniversityMax = generalSettings.pollMonthsInUniversityMax;
        this.pollMonthsLecturePeriod = generalSettings.pollMonthsLecturePeriod;
        this.pollMonthsLecturePeriodMax = generalSettings.pollMonthsLecturePeriodMax;
        this.pollMonthsLectureFreePeriod = generalSettings.pollMonthsLectureFreePeriod;
        this.pollMonthsLectureFreePeriodMax = generalSettings.pollMonthsLectureFreePeriodMax;
        this.pollPresenceDaysInLecturePeriod = generalSettings.pollPresenceDaysInLecturePeriod;
        this.pollPresenceDaysInLecturePeriodMax = generalSettings.pollPresenceDaysInLecturePeriodMax;
        this.pollMonthsLectureFreePeriodMax = generalSettings.pollMonthsLectureFreePeriodMax;
        this.pollPresenceDaysInLectureFreePeriod = generalSettings.pollPresenceDaysInLectureFreePeriod;
        this.pollPresenceDaysInLectureFreePeriodMax = generalSettings.pollPresenceDaysInLectureFreePeriodMax;

      }
    },
    /***
     * create date object from a date string
     */
    setPollDate(val) {
      if (!val) {
        return;
      }
      const dateParts = val.split(".");

      let month, day, year;
      if (dateParts.length === 3) {
        day = Number(dateParts[0]);
        month = Number(dateParts[1]);
        year = Number(dateParts[2]);
      }
      let dateBuilder = new Date();
      dateBuilder.setFullYear(year);
      dateBuilder.setMonth(month - 1);
      dateBuilder.setDate(day);
      this.pollDate = dateBuilder;
    },
    setUsePoll(val) {
      this.usePoll = val;
    },
    setFrequencyType(val) {
      this.frequencyType = val;
    },
    setClientName(val) {
      this.clientName = val;
    },
    setPollType(val) {
      this.pollType = val;
    },
    setTitle(val) {
      this.title = val;
    },
    setExternalPollLink(val) {
      this.externalPollLink = val;
    },
    setUseExternalPollLink(val) {
      this.useExternalPollLink = val;
    },
    setAlternativeExternalPollLink(val) {
      this.alternativeExternalPollLink = val;
    },
    setUseAlternativeExternalPollLink(val) {
      this.useAlternativeExternalPollLink = val;
    },
    setIntroductionSteps(val) {
      this.introductionSteps = val;
    },
    setIntroductionText(val) {
      this.introductionText = val;
    },
    setIntroductionTitle(val) {
      this.introductionTitle = val;
    },
    setShowCustomerLogo(val) {
      this.showCustomerLogo = val;
    },
    setSections(val) {
      this.sections = val;
    },
    setRequirePlz(val) {
      this.requirePlz = val === true;
    },
    setRequireStarsForUsedTransports(val) {
      this.requireStarsForUsedTransports = val === true;
    },
    setRequireStarsForAllTransports(val) {
      this.requireStarsForAllTransports = val === true;
    },
    setPollWorkingDays(val) {
      this.pollWorkingDays = val;
    },
    setPollHomeofficeDays(val) {
      this.pollHomeofficeDays = val;
    },
    setPollMonthsInOrganization(val) {
      this.pollMonthsInOrganization = val;
    },
    setPlz(plzStr) {
      this.pollPlz = plzStr;
    },
    /**
     * 
     * @param {*} obj contains freq (=frequency) and transportId 
     */
    addFrequency(obj) {
      this.frequencies.push(obj);
    },
    addNumberStars(obj) {
      this.numberStars.push(obj)
    },
    setNumberStars(num, transportId) {
      let numStars = this.numberStars.find(s => s.transportId === transportId);
      numStars.freq = num;
    },
    resetFrequencies() {
      this.frequencies = [];
    },
    setFrequency(num, transportId) {
      let freq = this.frequencies.find(f => f.transportId === transportId);
      freq.freq = num;
    },
    incrementFrequency(transportId) {
      let freq = this.frequencies.find(f => f.transportId === transportId)
      freq.freq += 1;
    },
    decrementFrequency(transportId) {
      let freq = this.frequencies.find(f => f.transportId === transportId)

      if (freq.freq > 0) {
        freq.freq -= 1;
      }
    },
    /**
     * 
     * @param {*} obj contains freq (=frequency) and transportId 
     */
    addNumberPassengers(obj) {
      this.numberPassengers.push(obj);
    },
    resetNumberPassengers() {
      this.numberPassengers = [];
    },
    setNumberPassengers(num, transportId) {
      let numPass = this.numberPassengers.find(p => p.transportId === transportId);
      numPass.freq = num;
    },
    incrementNumberPassengers(transportId) {
      let numPass = this.numberPassengers.find(p => p.transportId === transportId);
      numPass.freq += 1;
    },
    decrementNumberPassengers(transportId) {
      let numPass = this.numberPassengers.find(p => p.transportId === transportId);
      if (numPass.freq > 0) {
        numPass.freq -= 1;
      }
    },
    addTransportUsed(obj) {
      this.transportUsed.push(obj);
    },
    resetTransportUsed() {
      this.transportUsed = [];
    },
    setTransportUsed(val, transportId) {
      let tUsed = this.transportUsed.find(t => t.transportId === transportId);
      if (tUsed) {
        tUsed.used = !tUsed.used;
      }

    },
  },
});
